import { useMemo, useState } from 'react'
import { Label, Pie, PieChart, Sector } from 'recharts'
import { Expand } from 'lucide-react'
import { PieSectorDataItem } from 'recharts/types/polar/Pie'
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from '@/components/ui/chart'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { generateColorScale } from '@/lib/colors'
import { CourseCombobox } from './CourseCombobox'

type UserDistributionChartProps = {
  courseChartData: { name: string; active: number; total: number; users: number }[]
  loading: boolean
}

const UserDistributionChart = ({ courseChartData, loading }: UserDistributionChartProps) => {
  const [activeCourse, setActiveCourse] = useState('')

  const { userDistributionConfig, pieChartData, totalUsers } = useMemo(() => {
    const config: ChartConfig = {}
    const courseNames = courseChartData.map((course) => course.name)
    const colors = generateColorScale(courseNames)

    const total = courseChartData.reduce((sum, course) => sum + course.users, 0)

    config['total'] = {
      label: 'Total',
      color: 'hsl(var(--primary))',
    }

    const data = courseChartData.map((course) => {
      config[course.name] = {
        label: course.name,
        color: colors[course.name],
      }
      return {
        name: course.name,
        users: course.users,
        fill: colors[course.name],
      }
    })

    return {
      userDistributionConfig: config,
      pieChartData: data,
      totalUsers: total,
    }
  }, [courseChartData])

  const renderActiveShape = ({ cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill }: PieSectorDataItem) => {
    return (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius! + 10}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={outerRadius! + 12}
          outerRadius={outerRadius! + 25}
          fill={fill}
        />
      </g>
    )
  }

  const renderChart = (size: 'small' | 'large') => (
    <ChartContainer
      config={userDistributionConfig}
      className={`mx-auto aspect-square w-full ${size === 'small' ? 'max-w-[18.75rem]' : 'max-w-[37.5rem]'}`}
    >
      <PieChart>
        <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
        <Pie
          data={pieChartData}
          dataKey="users"
          nameKey="name"
          innerRadius={size === 'small' ? 70 : 100}
          outerRadius={size === 'small' ? 110 : 150}
          activeIndex={activeCourse ? pieChartData.findIndex((item) => item.name === activeCourse) : -1}
          activeShape={renderActiveShape}
        >
          <Label
            content={({ viewBox }) => {
              if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                const displayValue = activeCourse
                  ? pieChartData.find((item) => item.name === activeCourse)?.users || 0
                  : totalUsers
                return (
                  <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="middle">
                    <tspan x={viewBox.cx} y={viewBox.cy} className="fill-foreground text-3xl font-bold">
                      {displayValue.toLocaleString()}
                    </tspan>
                    <tspan x={viewBox.cx} y={(viewBox.cy || 0) + 24} className="fill-muted-foreground">
                      Users
                    </tspan>
                  </text>
                )
              }
            }}
          />
        </Pie>
      </PieChart>
    </ChartContainer>
  )

  return (
    <Dialog>
      <Card className="w-full min-w-0 lg:w-auto">
        <CardHeader className="flex flex-col items-start justify-between space-y-2 pb-2">
          <div className="flex items-center justify-between w-full">
            <CardTitle>User Distribution</CardTitle>
            <DialogTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0 data-[state=open]:bg-muted" aria-label="Open">
                <Expand className="h-4 w-4" />
              </Button>
            </DialogTrigger>
          </div>
          <div className="flex items-center justify-between w-full">
            <CardDescription>Users per Course</CardDescription>
            <CourseCombobox courses={pieChartData} activeCourse={activeCourse} setActiveCourse={setActiveCourse} />
          </div>
        </CardHeader>
        <CardContent className="min-w-0">
          {loading ? <Skeleton className="h-[18.75rem] w-full" /> : renderChart('small')}
        </CardContent>
      </Card>
      <DialogContent className="sm:max-w-[90%] max-h-[80vh] overflow-auto">
        <DialogHeader className="flex flex-row items-center justify-between">
          <CardHeader className="p-0">
            <DialogTitle asChild>
              <CardTitle>User Distribution</CardTitle>
            </DialogTitle>
            <DialogDescription asChild>
              <CardDescription>Users per Course</CardDescription>
            </DialogDescription>
          </CardHeader>
        </DialogHeader>
        <div className="flex items-center justify-center w-full">
          <CourseCombobox
            courses={pieChartData}
            activeCourse={activeCourse}
            setActiveCourse={setActiveCourse}
            width="w-[15rem]"
          />
        </div>
        {loading ? <Skeleton className="h-[37.5rem] w-full" /> : renderChart('large')}
      </DialogContent>
    </Dialog>
  )
}

export default UserDistributionChart
